import * as Sentry from "@sentry/react";
import {
  PARTNER_ERROR_CODES,
  PartnerAccessError,
  createPartnerSession,
  getGroupSummary,
  revokePartnerSession,
} from "./partnerApi";
import { getPartnerSessionId, isSedeMode, setPartnerSessionId } from "./partnerStorage";
import { invalidateDelegatedToken } from "./effectiveToken";
import { isPartnerAccessEnabled } from "./partnerFlag";

// ---------------------------------------------------------------------------
// Who the user is as a partner, and which sedes they can work.
//
// The sede list only comes with a NEW partner session (`POST /session`, 10 a
// minute, 20 live sessions per user), so the answer is cached per tab in
// `sessionStorage`, bound to the user it was given to. Like the rest of
// `partnerAccess`, nothing here writes to the storage of the normal session.
//
// The cache key is the redux user id (`state.user.data.id`). In a sede tab
// `/verifyToken` still answers the partner's own user id, so the key is the
// same in normal and in sede mode: entering or leaving a sede keeps the cache.
// ---------------------------------------------------------------------------

const STORAGE_PREFIX = "partnerAccess.";
export const PARTNER_DIRECTORY_KEY = `${STORAGE_PREFIX}directory`;
export const GROUP_SUMMARY_KEY = `${STORAGE_PREFIX}summary`;

// Used when the session answer carries no readable `expiresAt`.
const SESSION_LIFETIME_MS = 12 * 60 * 60 * 1000;
// The backend asks not to poll the summary faster than this.
const SUMMARY_MIN_INTERVAL_MS = 15 * 1000;

const COMPANY_ID_PATTERN = /^[0-9a-f]{24}$/;
const SENTRY_COMPONENT = "partnerAccess";
// Answers that mean "this user has no sedes": nothing is shown.
const NOT_A_PARTNER_CODES = [
  PARTNER_ERROR_CODES.NOT_A_PARTNER,
  PARTNER_ERROR_CODES.FEATURE_DISABLED,
];

const NONE = { status: "none" };
const UNAVAILABLE = { status: "unavailable" };

// { userId, promise } while a directory request is in flight.
let pendingDirectory = null;
let pendingSummary = null;
// Bumped when the tab is cleared: a request that started before must not
// write its late answer into the storage of whoever uses the tab next.
let generation = 0;

const readJson = (key) => {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw === null ? null : JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Without a cache the next open asks again.
  }
};

const text = (value) => (typeof value === "string" ? value : "");

// A channel platform is a name here and an object with `name` in the spaces
// list: both are accepted.
const platformName = (platform) => text(platform?.name ?? platform).toUpperCase();

const toChannel = (channel) => ({
  phone: text(channel?.phone) || (Number.isFinite(channel?.phone) ? String(channel.phone) : ""),
  platform: platformName(channel?.platform),
  isActive: channel?.isActive === true,
});

const toSede = (sede) => ({
  companyId: sede.companyId,
  label: text(sede.label) || text(sede.company?.name),
  monogram: text(sede.monogram),
  color: text(sede.color),
  channels: Array.isArray(sede.channels) ? sede.channels.map(toChannel) : [],
});

const toDirectory = (session) => {
  const sessionId = session?.sessionId;
  if (typeof sessionId !== "string" || sessionId === "" || !Array.isArray(session.sedes)) {
    throw new Error("Partner session response is malformed");
  }
  const expiresAt = Date.parse(session.expiresAt);
  return {
    sessionId,
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now() + SESSION_LIFETIME_MS,
    groupName: text(session.group?.name),
    sedes: session.sedes
      .filter((sede) => COMPANY_ID_PATTERN.test(text(sede?.companyId)))
      .map(toSede),
  };
};

const toPartnerResult = ({ groupName, sedes, sessionId }) => ({
  status: "partner",
  groupName,
  sedes,
  sessionId,
});

const cachedResultFor = (userId) => {
  const cached = readJson(PARTNER_DIRECTORY_KEY);
  if (cached === null || typeof cached !== "object" || cached.userId !== userId) return null;
  if (cached.status === "none") return NONE;
  if (cached.status !== "partner" || !Array.isArray(cached.sedes)) return null;
  if (!(cached.expiresAt > Date.now())) return null;

  // Token minting may have renewed the session meanwhile: the id the tab is
  // using wins. A tab that no longer holds one gets the cached one back.
  const storedSessionId = getPartnerSessionId();
  if (storedSessionId === null) setPartnerSessionId(cached.sessionId);
  return toPartnerResult({ ...cached, sessionId: storedSessionId || cached.sessionId });
};

/**
 * What this tab already knows about the sedes of `userId`, synchronously:
 * `{ groupName, sedes }` or `null`. It only reads the cache of the tab: no
 * request, no write. For UI that must not cost a partner session.
 */
export const peekPartnerDirectory = ({ userId } = {}) => {
  if (!userId) return null;
  const cached = readJson(PARTNER_DIRECTORY_KEY);
  if (cached === null || typeof cached !== "object" || cached.userId !== userId) return null;
  if (cached.status !== "partner" || !Array.isArray(cached.sedes)) return null;
  if (!(cached.expiresAt > Date.now())) return null;
  return { groupName: text(cached.groupName), sedes: cached.sedes };
};

// A new session took the place of the one the tab held (a sede tab with no
// directory cache got its session from token minting). The old one would stay
// alive until it expires and count against the live sessions of the user, and
// the delegated token in memory was minted under it: the token is dropped so
// the next request mints with the new session, and the old session is revoked.
// Revoking it is safe because the id a tab holds is exclusive to that tab: it
// lives in its `sessionStorage`, a new tab is handed only the company id
// (`openSedeInNewTab`) and mints a session of its own.
// Best effort: a failed revoke is never surfaced. Never rejects.
const releaseReplacedSession = async (previousSessionId, sessionId) => {
  if (previousSessionId === null || previousSessionId === sessionId) return;
  invalidateDelegatedToken();
  try {
    await revokePartnerSession(previousSessionId);
  } catch (e) {
    // The session expires on its own (12 h).
  }
};

const requestDirectory = async (userId) => {
  const startedIn = generation;
  try {
    const found = toDirectory(await createPartnerSession());
    if (startedIn === generation) {
      const previousSessionId = getPartnerSessionId();
      setPartnerSessionId(found.sessionId);
      writeJson(PARTNER_DIRECTORY_KEY, { userId, status: "partner", ...found });
      // Not awaited: the menu does not wait for the revoke.
      releaseReplacedSession(previousSessionId, found.sessionId);
    }
    return toPartnerResult(found);
  } catch (error) {
    const code = error instanceof PartnerAccessError ? error.code : null;
    if (NOT_A_PARTNER_CODES.includes(code)) {
      if (startedIn === generation) writeJson(PARTNER_DIRECTORY_KEY, { userId, status: "none" });
      return NONE;
    }
    // An API failure is an expected outage; anything else is a bug.
    if (code === null) {
      try {
        Sentry.captureException(error, {
          tags: { component: SENTRY_COMPONENT, op: "loadPartnerDirectory" },
        });
      } catch (e) {
        // Telemetry must never change what the menu shows.
      }
    }
    return UNAVAILABLE;
  }
};

/**
 * Resolves `{ status: "partner", groupName, sedes, sessionId }`,
 * `{ status: "none" }` (not a partner, feature off) or
 * `{ status: "unavailable" }` (may work on a later call). Never rejects.
 */
export const loadPartnerDirectory = async ({ userId } = {}) => {
  if (!isPartnerAccessEnabled()) return NONE;
  if (!userId) return UNAVAILABLE;

  const cached = cachedResultFor(userId);
  if (cached !== null) return cached;
  if (pendingDirectory !== null && pendingDirectory.userId === userId) {
    return pendingDirectory.promise;
  }

  const promise = requestDirectory(userId).finally(() => {
    if (pendingDirectory !== null && pendingDirectory.promise === promise) pendingDirectory = null;
  });
  pendingDirectory = { userId, promise };
  return promise;
};

const toSummary = (summary) => {
  if (!Array.isArray(summary?.sedes)) throw new Error("Group summary response is malformed");
  return summary.sedes.reduce((bySede, sede) => {
    if (!COMPANY_ID_PATTERN.test(text(sede?.companyId))) return bySede;
    return {
      ...bySede,
      [sede.companyId]: { waiting: Number(sede.waiting) || 0, late: Number(sede.late) || 0 },
    };
  }, {});
};

const requestSummary = async (sessionId) => {
  const startedIn = generation;
  let bySede = null;
  try {
    bySede = toSummary(await getGroupSummary(sessionId));
  } catch (e) {
    // The counters are a courtesy: the list works without them.
  }
  // A failure is kept too, so an outage is not asked about on every open.
  if (startedIn === generation) writeJson(GROUP_SUMMARY_KEY, { at: Date.now(), bySede });
  return bySede;
};

/**
 * Resolves `{ [companyId]: { waiting, late } }`, or `null` when the counters
 * are not available. At most one request every 15 s per tab: in between it
 * resolves the last answer. Never rejects.
 */
export const loadGroupSummary = async () => {
  if (!isPartnerAccessEnabled()) return null;
  const sessionId = getPartnerSessionId();
  if (sessionId === null) return null;

  const last = readJson(GROUP_SUMMARY_KEY);
  if (last !== null && Date.now() - last.at < SUMMARY_MIN_INTERVAL_MS) return last.bySede ?? null;
  if (pendingSummary !== null) return pendingSummary;

  const promise = requestSummary(sessionId).finally(() => {
    if (pendingSummary === promise) pendingSummary = null;
  });
  pendingSummary = promise;
  return promise;
};

/**
 * Drops the cached directory of the tab, and with it the partner session it
 * names, so the next `loadPartnerDirectory` opens a new session. For a
 * service that rejected the session (the statistics API answering 401). The
 * rejected session is revoked when the new one replaces it. Never throws.
 */
export const forgetPartnerDirectory = () => {
  pendingDirectory = null;
  try {
    window.sessionStorage.removeItem(PARTNER_DIRECTORY_KEY);
  } catch (e) {
    // A storage that cannot be used holds no directory.
  }
};

/** Whether this tab holds anything a logout has to end on the partner side. */
export const hasPartnerAccessState = () => isSedeMode() || getPartnerSessionId() !== null;

/**
 * Forgets everything partner access keeps in this tab: sede mode, partner
 * session id, directory, counters, pending notice and the delegated token.
 */
export const clearPartnerAccessState = () => {
  generation += 1;
  pendingDirectory = null;
  pendingSummary = null;
  invalidateDelegatedToken();
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch (e) {
    // A storage that cannot be used holds no partner state.
  }
};

/**
 * For logout: revokes the partner session of the tab (best effort, a failure
 * is not an error for the user) and then clears the tab. Never rejects.
 */
export const endPartnerAccess = async () => {
  const sessionId = getPartnerSessionId();
  try {
    if (sessionId !== null) await revokePartnerSession(sessionId);
  } catch (e) {
    // The session expires on its own (12 h); logout must go on.
  }
  clearPartnerAccessState();
};
